import React, { useCallback, useMemo, useRef, useState } from 'react';
import ReactTooltip from 'react-tooltip';
import PropTypes from 'prop-types';

import Spinner from '../../shared/spinner.component';
import AgGrid from '../../shared/agGrid/agGrid.component';
import ConfirmDialog from '../../shared/confirmDialog.component';
import { columnsToolPanel, filtersToolPanel } from '../../shared/gridEdit/toolPanels';
import { GridMode, useEditableGridLogic } from '../../shared/gridEdit/useEditableGridLogic';
import NavigationPrompt from '../../shared/navigationPrompt.component';
import { LookUpDataPropTypes, ObligationPropTypes } from './propTypes';
import { YES_FLAG } from '../../shared/constants';
import ConfirmationDialogContent from './confirmationDialogContent.component';
import WarningDialog from '../../shared/warningDialog.component';
import { useDialogState } from '../../shared/hooks/useDialogState';

import getColumnDefinitionsForEditView from './columnDefinitionsForEditView';
import {
  shouldWarnAboutClearingBSMBalances,
  shouldWarnAboutDisablingRelatedUSObligations,
  validateObligation,
} from './validators';
import { companyCodeRelatedFields } from './constants';
import { createObligationRow, getObligationUniqueId } from './helpers';
import DeleteObligationModal from './deleteObligationModal.component';
import { PeriodPropTypes } from '../../shared/propTypes/period';
import { useApplyFiltersInitialValue } from '../../scenarios/shared/applyFiltersInitialValue';
import { mergeColumnDefinitionsField } from '../../utils/columnDefinitionUtils';
import { sortBySeverity } from '../../shared/sortBySeverity';
import EditableGridButtons from '../../shared/gridEdit/EditableGridButtons.component';
import { useCheckTaxRatesToChangeAfterObligationUpdate } from '@mutations/obligations';
import { AlertOverlay } from '../../shared/Overlay.component';

export const CheckingTaxRatesToChangeOverlay = ({ children, showOverlay }) => (
  <AlertOverlay
    show={showOverlay}
    message={
      <div className="d-flex align-items-center">
        <Spinner small className="spinner-no-margin d-flex mr-2"/>
        Please Wait - A deferred rate change was detected and the rate change entries are being determined
      </div>
    }
    type="alert-info"
  >
    { children }
  </AlertOverlay>
);

CheckingTaxRatesToChangeOverlay.propTypes = {
  children: PropTypes.node.isRequired,
  showOverlay: PropTypes.bool,
};

const ObligationEditViewGrid = props => {
  const {
    period,

    obligations,
    isLoadingObligations,
    lookUpData,
    isLoadingLookUpData,

    updateObligation,
    isUpdatingObligations,

    createObligation,
    isCreatingObligations,

    canModifyAllObligations,
    companyCodesSet,
    canModifyInSelectedPeriod,
    canEditTaxCurrencySubledger,

    deleteModalData,
    hideObligationModalForDelete,
    removeObligation,
    isRemovingObligation,

    initialFilters,
  } = props;

  const [mode, setMode] = useState(GridMode.View);
  const shouldAddRef = useRef(false);
  const [isReseting, setReseting] = useState(false);

  const {
    showDialog: showCancelDialog,
    hideDialog: hideCancelDialog,
    isDialogVisible: isCancelDialogVisible,
  } = useDialogState();

  const {
    showDialog: showSaveDialog,
    hideDialog: hideSaveDialog,
    isDialogVisible: isSaveDialogVisible,
    dialogData,
  } = useDialogState();

  const {
    checkTaxRatesToUpdate,
    isLoading: isCheckingTaxRatesToChange,
  } = useCheckTaxRatesToChangeAfterObligationUpdate();

  const shouldShowNavigationPrompt = mode === GridMode.EditAll || mode === GridMode.Add || mode === GridMode.Edit;

  const onSave = useCallback(
    async ({ updatedRows, addedRows, hasErrors, reset }) => {
      if (hasErrors) {
        return;
      }
      const updatedRow = updatedRows[0];
      const addedRow = addedRows[0];
      if (mode === GridMode.Edit && updatedRow) {
        const { touched } = updatedRow.meta;
        const { companyCode, countryCode, countryTaxType, deferredTaxRate } = updatedRow;
        const { subledgerFlag } = lookUpData.companyCodeOptions.find(({ value }) => value === companyCode) || {};

        const { taxRatesToUpdate, unapprovedEntryCount } = touched.deferredTaxRate
          ? await checkTaxRatesToUpdate([
            { companyCode, countryCode, countryTaxType, deferredTaxRate },
          ])
          : { taxRatesToUpdate: 0, unapprovedEntryCount: 0 };

        const shouldWarnAboutBatchUpdate = companyCodeRelatedFields.some(field => touched[field]);
        const shouldWarnAboutCurrencyEdit = subledgerFlag === YES_FLAG && touched.taxCurrency;
        const shouldWarnAboutSecondaryCurrencyEdit = updatedRow.secondaryTax === '' && touched.secondaryTax;
        const warnAboutBSMBalances = shouldWarnAboutClearingBSMBalances(updatedRow, lookUpData, obligations);
        const warnAboutDisablingRelatedUSObligation = shouldWarnAboutDisablingRelatedUSObligations(
          updatedRow, lookUpData, obligations
        );

        if (shouldWarnAboutCurrencyEdit
          || shouldWarnAboutSecondaryCurrencyEdit
          || shouldWarnAboutBatchUpdate
          || warnAboutBSMBalances
          || warnAboutDisablingRelatedUSObligation
          || taxRatesToUpdate > 0
          || unapprovedEntryCount > 0
        ) {
          showSaveDialog({
            shouldWarnAboutBatchUpdate,
            shouldWarnAboutCurrencyEdit,
            shouldWarnAboutSecondaryCurrencyEdit,
            shouldWarnAboutBSMBalances: warnAboutBSMBalances,
            shouldWarnAboutDisablingRelatedUSObligation: warnAboutDisablingRelatedUSObligation,
            changedRow: updatedRow,
            taxRatesToUpdate,
            unapprovedEntryCount,
          });
          return;
        }
        setMode(GridMode.View);
        updateObligation(updatedRow, { resetEditableGrid: reset });
        return;
      }
      if (mode === GridMode.Edit && !updatedRow) {
        setMode(GridMode.View);
        return;
      }
      if (mode === GridMode.Add && addedRow) {
        const { touched } = addedRow.meta;
        const shouldWarnAboutBatchUpdate = companyCodeRelatedFields.some(field => touched[field]);
        if (shouldWarnAboutBatchUpdate) {
          showSaveDialog({
            shouldWarnAboutBatchUpdate,
            changedRow: addedRow,
          });
          return;
        }
        setMode(GridMode.View);
        createObligation(addedRow, { resetEditableGrid: reset });
        return;
      }
    },
    [mode, updateObligation, createObligation, showSaveDialog, lookUpData, obligations, checkTaxRatesToUpdate]
  );

  const onReset = () => {
    setReseting(false);
  };

  const onCancel = (hasChanges, reset) => {
    if (hasChanges) {
      showCancelDialog();
    } else {
      setMode(GridMode.View);
      reset();
    }
  };

  const {
    onGridReady,
    save,
    cancel,
    reset,
    setRowData,
    addRow,
    getRowNodeId,
    processCellFromClipboard,
    editedRowId,
    setEditedRow,
    postSort,
    onSortChanged,
    onModelUpdated,
    onSelectionChanged,
    clearRangeSelection,
  } = useEditableGridLogic({
    createRow: createObligationRow,
    data: obligations,
    getRowUniqueId: getObligationUniqueId,
    mode,
    validateRow: validateObligation,
    onSave,
    onReset,
    onCancel,
    context: {
      ...lookUpData,
      existingObligations: obligations,
    },
    includeMetaWhenSaving: true,
  });

  const confirmCancel = useCallback(() => {
    setReseting(true);
    reset();
    hideCancelDialog();
    setMode(GridMode.View);
  }, [reset, hideCancelDialog, setMode, setReseting]);

  const confirmSave = useCallback(async () => {
    const { meta, ...rest } = dialogData.changedRow;
    hideSaveDialog();
    setMode(GridMode.View);
    if (meta.isUpdated) {
      updateObligation(rest, { resetEditableGrid: reset });
    } else if (meta.isAdded) {
      createObligation(rest, { resetEditableGrid: reset });
    }
  }, [updateObligation, createObligation, reset, setMode, dialogData, hideSaveDialog]);

  const editRow = useCallback(data => {
    setMode(GridMode.Edit);
    setEditedRow(data);
  }, [setEditedRow]);

  const onAddClick = useCallback(() => {
    setMode(GridMode.Add);
    shouldAddRef.current = true;
  }, [setMode]);

  const onSaveClick = useCallback(() => {
    save();
  }, [save]);

  const onCancelClick = useCallback(() => {
    cancel();
  }, [cancel]);

  const onFirstDataRenderedBase = useApplyFiltersInitialValue(initialFilters);

  const onFirstDataRendered = useCallback(params => {
    onFirstDataRenderedBase?.(params);
    if (mode === GridMode.Add && shouldAddRef.current) {
      addRow();
      shouldAddRef.current = false;
    }
  }, [onFirstDataRenderedBase, mode, addRow]);

  const context = useMemo(() => ({
    mode,
    editedRowId,
    canModifyAllObligations,
    companyCodesSet,
    canModifyInSelectedPeriod,
    canEditTaxCurrencySubledger,
  }), [
    mode,
    editedRowId,
    canModifyAllObligations,
    companyCodesSet,
    canModifyInSelectedPeriod,
    canEditTaxCurrencySubledger,
  ]);

  const columnDefinitions = useMemo(() => {
    const base = getColumnDefinitionsForEditView({
      setRowData,
      editRow,
      lookUpData,
      context,
    });

    if (mode === GridMode.Add) {
      return base;
    }

    return mergeColumnDefinitionsField(['severity'], {
      sort: 'asc',
      comparator: (a, b) => sortBySeverity({ severity: a }, { severity: b }),
    })(base);
  }, [
    mode,
    setRowData,
    editRow,
    lookUpData,
    context,
  ]);

  const isGridLoading = isLoadingObligations
    || isLoadingLookUpData
    || isUpdatingObligations
    || isCreatingObligations
    || isReseting;

  return (
    <div className="ag-grid-full-page-with-secondary-menu">
      {
        canModifyInSelectedPeriod && (canModifyAllObligations || companyCodesSet.size)
          ? <EditableGridButtons
            mode={mode}
            disabled={isGridLoading}
            onAddClick={onAddClick}
            onCancelClick={onCancelClick}
            onSaveClick={onSaveClick}
          />
          : null
      }
      <CheckingTaxRatesToChangeOverlay showOverlay={isCheckingTaxRatesToChange}>
        <AgGrid
          key={mode === GridMode.Add ? 'grid-add' : 'grid-base'}
          className="ag-theme-bootstrap"
          columnDefs={columnDefinitions}
          isGridLoading={isGridLoading}
          onGridReady={onGridReady}
          getRowId={getRowNodeId}
          processCellFromClipboard={processCellFromClipboard}
          enableBrowserTooltips
          stopEditingWhenCellsLoseFocus
          suppressCellFocus
          suppressMultiRangeSelection
          enableRangeSelection
          suppressCopyRowsToClipboard
          suppressClipboardPaste
          suppressScrollOnNewData
          sideBar={{
            toolPanels: [
              columnsToolPanel,
              filtersToolPanel,
            ],
          }}
          context={context}
          postSort={postSort}
          onSortChanged={onSortChanged}
          onSelectionChanged={onSelectionChanged}
          onFirstDataRendered={onFirstDataRendered}
          withSearchBar
          onModelUpdated={onModelUpdated}
          popupParent={document.querySelector('.obligations-page')}
        />
      </CheckingTaxRatesToChangeOverlay>
      <ReactTooltip place="right" effect="float" />
      <ConfirmDialog
        onCancel={hideCancelDialog}
        onConfirm={confirmCancel}
        message="Are you sure you want to discard the changes?"
        title="Confirm Cancel"
        dismissText="No"
        submitText="Yes"
        isVisible={isCancelDialogVisible}
      />

      <WarningDialog
        onConfirm={confirmSave}
        onCancel={hideSaveDialog}
        message={<ConfirmationDialogContent dialogData={dialogData || {}} />}
        title="Warning!"
        dismissText="Cancel"
        submitText="Submit"
        isVisible={isSaveDialogVisible}
      />
      <NavigationPrompt show={shouldShowNavigationPrompt} />
      <DeleteObligationModal
        entityToRemove={deleteModalData.data && deleteModalData.data.entity}
        obligations={obligations}
        lookUpData={lookUpData}
        hideModal={hideObligationModalForDelete}
        removeEntity={removeObligation}
        rowIndex={deleteModalData.data && deleteModalData.data.rowIndex}
        isRemovingInProgress={isRemovingObligation}
        isVisible={deleteModalData.show}
        clearRangeSelection={clearRangeSelection}
        period={period}
      />
    </div>
  );
};


ObligationEditViewGrid.propTypes = {
  period: PeriodPropTypes,

  obligations: PropTypes.arrayOf(ObligationPropTypes).isRequired,
  isLoadingObligations: PropTypes.bool.isRequired,

  lookUpData: LookUpDataPropTypes.isRequired,
  isLoadingLookUpData: PropTypes.bool.isRequired,

  updateObligation: PropTypes.func.isRequired,
  isUpdatingObligations: PropTypes.bool.isRequired,

  createObligation: PropTypes.func.isRequired,
  isCreatingObligations: PropTypes.bool.isRequired,

  canModifyInSelectedPeriod: PropTypes.bool.isRequired,
  canModifyAllObligations: PropTypes.bool.isRequired,
  companyCodesSet: PropTypes.instanceOf(Set).isRequired,

  canEditTaxCurrencySubledger: PropTypes.bool.isRequired,

  deleteModalData: PropTypes.shape({
    show: PropTypes.bool.isRequired,
    data: PropTypes.shape({
      rowIndex: PropTypes.number,
      entity: ObligationPropTypes,
    }),
  }),
  hideObligationModalForDelete: PropTypes.func.isRequired,
  removeObligation: PropTypes.func.isRequired,
  isRemovingObligation: PropTypes.bool,

  initialFilters: PropTypes.object, // eslint-disable-line react/forbid-prop-types
};

export default ObligationEditViewGrid;
